import { skillEffectiveCooldownMs } from "@/character/skills/cooldown-upgrade";
import { SKILL_SLOT_MAX, formatSkillCooldownSeconds } from "@/character/skills/constants";
import { skillById } from "@/character/skills/storage";
import type { SkillBarState } from "@/character/skills/types";
import {
  formatSkillSlotCooldownLabel,
  skillSlotCooldownOverlayHtml,
} from "@/character/skills/skill-bar-cooldown";
import { HOME_ASSETS } from "@/home/home-assets";
import { TowerEvents } from "@/tower/tower-events";

function escapeHtml(text: string): string {
  return text
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;");
}

export type PlaySkillBarRenderOptions = {
  skillBar: SkillBarState;
  unlocked: number;
  skillCooldownRemainingMs: (slotIndex: number) => number;
  disabled?: boolean;
  lockedTitle?: string;
  offClass?: string;
};

export function renderPlaySkillBarSlotsHtml(options: PlaySkillBarRenderOptions): string {
  const { skillBar, unlocked, skillCooldownRemainingMs, disabled, lockedTitle, offClass } =
    options;
  return Array.from({ length: SKILL_SLOT_MAX }, (_, index) => {
    const locked = index >= unlocked;
    const skillId = skillBar.slots[index];
    const skill = skillId ? skillById(skillBar, skillId) : undefined;
    const cdLeft = skillCooldownRemainingMs(index);
    const onCd = cdLeft > 0;
    const cardBadge = skill?.cooldownCards ?? 0;
    if (locked) {
      return `<div class="home-skill-slot-col">
        <button type="button" class="home-skill-slot home-skill-slot--locked" disabled title="${escapeHtml(lockedTitle ?? "ล็อกช่องสกิล")}">
          <span class="home-skill-slot-key" aria-hidden="true">${index + 1}</span>
          <span class="home-skill-slot-lock" aria-hidden="true">🔒</span>
        </button>
        <span class="home-skill-slot-badge home-skill-slot-badge--muted" aria-hidden="true">—</span>
      </div>`;
    }
    if (!skill) {
      return `<div class="home-skill-slot-col">
        <button type="button" class="home-skill-slot home-skill-slot--empty${offClass ? ` ${offClass}` : ""}" data-skill-slot="${index}" title="ช่องว่าง" ${disabled ? "disabled" : ""}>
          <span class="home-skill-slot-key" aria-hidden="true">${index + 1}</span>
        </button>
        <span class="home-skill-slot-badge home-skill-slot-badge--muted" aria-hidden="true">0</span>
      </div>`;
    }
    const kindLabel = skill.kind === "buff" ? "บัฟ" : "โจมตี";
    const cdMs = skillEffectiveCooldownMs(skill);
    const autoOn = skillBar.slotAuto[index] === true;
    const autoHint = autoOn ? " · Auto" : "";
    const cdOverlay = onCd
      ? skillSlotCooldownOverlayHtml(formatSkillSlotCooldownLabel(cdLeft))
      : "";
    const cdClass = onCd ? " home-skill-slot--cooldown" : "";
    const autoClass = autoOn ? " home-skill-slot--auto" : "";
    const off = offClass ? ` ${offClass}` : "";
    return `<div class="home-skill-slot-col">
      <button type="button" class="home-skill-slot${cdClass}${autoClass}${off}" data-skill-slot="${index}" data-skill-id="${escapeHtml(skill.id)}" title="${escapeHtml(skill.name)} · ${kindLabel} · คูลดาวน์ ${formatSkillCooldownSeconds(cdMs)}วิ${autoHint}" ${onCd || disabled ? "disabled" : ""}>
        <span class="home-skill-slot-key" aria-hidden="true">${index + 1}</span>
        <img src="${escapeHtml(skill.iconDataUrl)}" alt="" width="32" height="32" />
        ${cdOverlay}
      </button>
      <span class="home-skill-slot-badge${cardBadge > 0 ? " home-skill-slot-badge--filled" : ""}" aria-hidden="true">${cardBadge}</span>
    </div>`;
  }).join("");
}

export function playSkillBarEligibleSkillCount(skillBar: SkillBarState, unlocked: number): number {
  let eligible = 0;
  for (let index = 0; index < SKILL_SLOT_MAX; index += 1) {
    if (index >= unlocked || !skillBar.slots[index]) continue;
    eligible += 1;
  }
  return eligible;
}

export function playSkillBarSkillAutoState(
  skillBar: SkillBarState,
  unlocked: number,
): { allOn: boolean; someOn: boolean; eligible: number } {
  let eligible = 0;
  let onCount = 0;
  for (let index = 0; index < SKILL_SLOT_MAX; index += 1) {
    if (index >= unlocked || !skillBar.slots[index]) continue;
    eligible += 1;
    if (skillBar.slotAuto[index]) onCount += 1;
  }
  return {
    eligible,
    allOn: eligible > 0 && onCount === eligible,
    someOn: onCount > 0,
  };
}

export function setAllPlaySkillBarAuto(
  skillBar: SkillBarState,
  unlocked: number,
  enabled: boolean,
): void {
  for (let index = 0; index < SKILL_SLOT_MAX; index += 1) {
    if (index >= unlocked || !skillBar.slots[index]) continue;
    skillBar.slotAuto[index] = enabled;
  }
}

export function emitPlaySkillBarMasterAutoCombat(enabled: boolean): void {
  window.dispatchEvent(
    new CustomEvent(TowerEvents.AutoChaseChanged, { detail: { enabled } }),
  );
}

export function setPlaySkillBarMasterAuto(
  skillBar: SkillBarState,
  unlocked: number,
  enabled: boolean,
): void {
  skillBar.masterAuto = enabled;
  if (enabled) {
    setAllPlaySkillBarAuto(skillBar, unlocked, true);
  } else {
    setAllPlaySkillBarAuto(skillBar, unlocked, false);
  }
}

export type PlaySkillBarBindings = {
  barSelector: string;
  getSkillBar: () => SkillBarState;
  getUnlocked: () => number;
  onUseSkill: (slotIndex: number, skillId: string) => void;
  onSkillBarUpdated: () => void;
  onMasterAutoChange?: (enabled: boolean) => void;
  getSitRest?: () => boolean;
  setSitRest?: (enabled: boolean) => void;
  onSitRestChange?: (enabled: boolean) => void;
};

export function bindPlaySkillBar(root: HTMLElement, bindings: PlaySkillBarBindings): () => void {
  const onClick = (event: Event) => {
    const btn = (event.target as HTMLElement).closest<HTMLButtonElement>("[data-skill-id]");
    if (!btn || btn.disabled) return;
    const skillId = btn.dataset.skillId;
    const slotIndex = Number(btn.dataset.skillSlot);
    if (!skillId || !Number.isFinite(slotIndex)) return;
    bindings.onUseSkill(slotIndex, skillId);
  };

  const onAutoClick = (event: Event) => {
    const btn = (event.target as HTMLElement).closest<HTMLButtonElement>("[data-skill-auto-toggle]");
    if (!btn || btn.disabled) return;
    event.preventDefault();
    event.stopPropagation();
    const bar = bindings.getSkillBar();
    const unlocked = bindings.getUnlocked();
    const next = bar.masterAuto !== true;
    if (next && bindings.getSitRest?.()) {
      bindings.setSitRest?.(false);
      syncPlaySkillBarSitRestUi(root, false);
    }
    setPlaySkillBarMasterAuto(bar, unlocked, next);
    bindings.onMasterAutoChange?.(next);
    bindings.onSkillBarUpdated();
  };

  const onSitClick = (event: Event) => {
    const btn = (event.target as HTMLElement).closest<HTMLButtonElement>("[data-skill-sit-toggle]");
    if (!btn || btn.disabled) return;
    if (!bindings.setSitRest) return;
    event.preventDefault();
    event.stopPropagation();
    const next = !bindings.getSitRest?.();
    if (next) {
      const bar = bindings.getSkillBar();
      const unlocked = bindings.getUnlocked();
      if (bar.masterAuto) {
        setPlaySkillBarMasterAuto(bar, unlocked, false);
        bindings.onMasterAutoChange?.(false);
        bindings.onSkillBarUpdated();
        syncPlaySkillBarMasterAutoUi(root, bar, unlocked);
      }
    }
    bindings.setSitRest(next);
    syncPlaySkillBarSitRestUi(root, next);
  };

  const bar = root.querySelector(bindings.barSelector);
  const hotbar = root.querySelector(".home-combat-hotbar");
  bar?.addEventListener("click", onClick);
  hotbar?.addEventListener("click", onAutoClick, true);
  hotbar?.addEventListener("click", onSitClick, true);

  return () => {
    bar?.removeEventListener("click", onClick);
    hotbar?.removeEventListener("click", onAutoClick, true);
    hotbar?.removeEventListener("click", onSitClick, true);
  };
}

export function syncPlaySkillBarMasterAutoUi(
  root: ParentNode,
  skillBar: SkillBarState,
  unlocked: number,
): void {
  const btn = root.querySelector<HTMLButtonElement>("[data-skill-auto-toggle]");
  if (!btn) return;
  const masterOn = skillBar.masterAuto === true;
  const { allOn, someOn, eligible } = playSkillBarSkillAutoState(skillBar, unlocked);
  btn.setAttribute("aria-pressed", masterOn ? "true" : "false");
  btn.classList.toggle("is-on", masterOn);
  btn.classList.toggle("is-partial", masterOn && eligible > 0 && someOn && !allOn);
}

export function syncPlaySkillBarSitRestUi(root: ParentNode, enabled: boolean): void {
  const btn = root.querySelector<HTMLButtonElement>("[data-skill-sit-toggle]");
  if (!btn) return;
  btn.setAttribute("aria-pressed", enabled ? "true" : "false");
  btn.classList.toggle("is-on", enabled);
}

export function playSkillBarRowMarkup(
  barId: string,
  autoInputId: string,
  extraBarClass = "",
): string {
  const navClass = extraBarClass ? `home-skill-bar ${extraBarClass}` : "home-skill-bar";
  return `<div class="home-combat-hotbar-row home-combat-hotbar-row--skills">
      <nav class="${navClass}" id="${barId}" aria-label="สกิล"></nav>
      <div class="home-combat-hotbar-actions-col" aria-label="โหมดต่อสู้และพัก">
        <button
          type="button"
          class="home-skill-bar-auto-master"
          id="${autoInputId}"
          data-skill-auto-toggle=""
          aria-pressed="false"
          title="โจมตีธรรมดาอัตโนมัติ · ยิงสกิลในช่องเมื่อคูลดาวน์พร้อม"
        >
          <span class="home-skill-bar-auto-icon" aria-hidden="true">
            <img
              class="home-skill-bar-auto-icon-img"
              src="${HOME_ASSETS.combatAutoSwords}"
              alt=""
              width="64"
              height="64"
              decoding="async"
            />
          </span>
          <span class="home-skill-bar-auto-label">Auto</span>
        </button>
        <button
          type="button"
          class="home-skill-bar-sit-master"
          data-skill-sit-toggle=""
          aria-pressed="false"
          title="นั่งพัก · ฟื้น HP และ MP เร็วขึ้น 2 เท่า"
        >
          <span class="home-skill-bar-auto-icon" aria-hidden="true">
            <img
            class="home-skill-bar-auto-icon-img home-skill-bar-sit-icon-img"
            src="${HOME_ASSETS.combatSitMeditate}"
              alt=""
              width="64"
              height="64"
              decoding="async"
            />
          </span>
          <span class="home-skill-bar-auto-label">นั่ง</span>
        </button>
      </div>
    </div>`;
}
